import { NumberFieldProps, NumberField as ShellNumberField } from '@e3dc-react/shell/Elements/InputFields/NumberField'
import React, { forwardRef, useCallback, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { TextField, Box, useMediaQuery, useTheme } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import { parseDecimalSeparator, isNumericInputAllowed, isBelowMinLimit, isAboveMaxLimit } from './NumberFieldUtils'
import { useSuffixFieldStyles } from '../../hooks/useSuffixFieldStyles'

const useStyles = makeStyles()(theme => ({
  input: {
    '& .MuiInputBase-root': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiOutlinedInput-input': {
      paddingRight: 0,
    },
  },
  suffix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 34,
    marginLeft: theme.spacing(2),
    paddingInline: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.shades.shade02}`,
    '& > span': {
      minWidth: 48,
      textAlign: 'center',
    },
  },
  emptyAdornment: {
    paddingRight: theme.spacing(2),
  },
}))

export interface LocalProps extends Partial<NumberFieldProps> {
  name: string
  textAlign?: 'left' | 'right' | 'center'
  fullWidth?: boolean
  isDecimal?: boolean
  step?: boolean extends LocalProps['isDecimal'] ? number : undefined
  precision?: boolean extends LocalProps['isDecimal'] ? number : undefined
  ignoreNonNumerics?: boolean
  ignoreDefaultValue?: boolean
}

const NumberField = forwardRef<typeof ShellNumberField, LocalProps>((props, ref) => {
  const {
    name,
    textAlign = 'right',
    fullWidth,
    prefix,
    suffix,
    isDecimal,
    step,
    precision,
    ignoreNonNumerics,
    ignoreDefaultValue,
    min,
    max,
    ...otherProps
  } = props
  const { classes, cx } = useStyles()
  const { classes: suffixFieldClasses } = useSuffixFieldStyles()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const useFullWidth = fullWidth || downSm
  const [field, meta] = useField(name as string)
  const { setFieldValue } = useFormikContext()
  const { i18n } = useTranslation()

  const appLanguage = i18n.language
  const defaultValue = ignoreDefaultValue ? '' : '0'
  const fieldValue = field.value ? parseDecimalSeparator(field.value.toString(), appLanguage) : defaultValue

  const hasError = useMemo<boolean>(
    () => {
      if (otherProps.disabled) return false
      return Boolean(meta.error && (meta.initialValue !== meta.value || meta.touched))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.name, meta]
  )

  const handleChangeEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      if (name) {
        setFieldValue(name, value.replace(',', '.'), true)
      } else {
        console.warn('Invalid fieldName', name)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      /* if the field is decimal and the user enters integer number e.g: 2, this event will set it to 2.00 */
      if (isDecimal && precision) {
        const originalValue = event.target.value
        if (originalValue === '') return

        const formattedValue = parseFloat(originalValue.replace(',', '.')).toFixed(precision)

        setFieldValue(name, formattedValue, true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, isDecimal]
  )

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (downSm) event.target.select()
  }

  const formatValue = useCallback(
    (value: number): string => {
      if (isDecimal && precision) {
        return value.toFixed(precision)
      }

      return value.toString()
    },
    [isDecimal, precision]
  )

  const handleArrowKeys = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      const currentValue = parseFloat(field.value) || 0
      const stepValue = step ?? 1
      let newValue = currentValue

      if (event.key === 'ArrowUp') {
        newValue = currentValue + stepValue
      } else if (event.key === 'ArrowDown') {
        newValue = currentValue - stepValue
      }

      if (isBelowMinLimit(newValue, min) || isAboveMaxLimit(newValue, max)) {
        return
      }

      setFieldValue(name, formatValue(newValue), true)
    },
    [step, min, max, setFieldValue, name, field.value, formatValue]
  )

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement
      const value = input.value
      const keyValue = event.key

      if (!isNumericInputAllowed(keyValue, value, isDecimal ?? false)) {
        event.preventDefault()
      }
    },
    [isDecimal]
  )

  return (
    <TextField
      {...otherProps}
      className={cx(classes.input, suffix && suffixFieldClasses.suffixField)}
      fullWidth={useFullWidth}
      type="text"
      InputProps={{
        endAdornment: suffix ? (
          <div className={classes.suffix}>
            <span>{suffix}</span>
          </div>
        ) : (
          <InputAdornment position="end" className={classes.emptyAdornment} />
        ),
        startAdornment: prefix && (
          <InputAdornment position="start" sx={{ position: 'absolute', ml: 1, top: '50%', transform: 'translateY(-50%)' }}>
            {<Box>{prefix}</Box>}
          </InputAdornment>
        ),
        inputProps: {
          style: { textAlign },
        },
      }}
      value={fieldValue || ''}
      onChange={handleChangeEvent}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleArrowKeys}
      onKeyPress={handleKeyPress}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      error={hasError}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      helperText={hasError ? meta.error : undefined}
      inputRef={ref}
    />
  )
})

NumberField.displayName = 'NumberField'

export default NumberField
